import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
// eslint-disable-next-line no-restricted-imports -- DS2026 Phosphor icon set for the sequence (Build) step
import {
  PiCaretDoubleLeft,
  PiCaretDoubleRight,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiCaretUp,
  PiChatText,
  PiCheck,
  PiClock,
  PiCopy,
  PiEye,
  PiPaperPlaneTilt,
  PiPlus,
  PiTrash,
  PiUser,
  PiWarning,
} from 'react-icons/pi'
import DOMPurify from 'dompurify'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import {
  fillVariables,
  detectUnfilledVariables,
  findCampaignColumnVariablesWithMissingValues,
  findUnknownTemplateVariables,
  getMissingValuesLabel,
  mergeVariables,
  type EmailVariable,
} from '../email-variables'
import { renderEmail } from '../../../lib/email-render/renderEmail'
import { Spinner } from 'react-bootstrap'
import axios from 'axios'

import { BaseTextInput } from 'components/FormUtils/TextInput'
import FbButton from 'components/FbUI/FbButton'
import { StatusBadge } from 'components/FbUI/StatusBadge'
import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import { cn } from 'components/UI/cn'

import {
  CreateSequencePayload,
  UpdateSequencePayload,
  useCreateSequence,
  useGetCampaignSequence,
  useUpdateSequence,
} from '../sequences_api'
import {
  type CampaignLeadReviewItem,
  useGetCampaignLeadReview,
} from '../campaigns_api'
import { SequenceForm, defaultStep } from '../SequenceTab/SequenceStepFormItem'
import { EmailBodyEditor } from '../SequenceTab/EmailBodyEditor'
import { useMountEffect } from 'hooks/useMountEffect'
import { useDebouncedFlag } from 'hooks/useDebouncedFlag'
import { handleFormError } from 'utils/handleFormError'
import { useGetCampaignSettings } from 'services/campaigns'
import { useGetEmailOverrides } from '../email_overrides_api'
import { useGetCampaignSenderEmails } from '../sender_emails_api'
import { FormatBadge } from '../FormatBadge'

import { LiveCampaignBanner } from './LiveCampaignBanner'
import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'
import { useCampaignAITokensByContact } from '../columns/useCampaignAITokens'
import { useCampaignVariables } from '../columns/useCampaignVariables'
import { SendStepTestEmailModal } from '../SendStepTestEmailModal'
import { SignatureDisclaimer } from '../SignatureDisclaimer'
import { applyPreviewDisplayFixes } from '../previewDisplayFixes'

interface WizardSequenceStepProps {
  campaignId: number
  campaignName: string
  bisonCampaignId?: number
  onNext: () => void
  onBack: () => void
  stepNumber: number
  totalSteps: number
  readOnly?: boolean
  isLiveCampaign?: boolean
  isActiveCampaign?: boolean
  backLabel?: string
}

interface PreviewContact {
  id: string
  bisonLeadId: number | null
  label: string
  subtitle: string
  tokens: Record<string, string>
}

type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error'

const SAVE_STATUS_LABEL: Record<SaveStatus, string> = {
  idle: '',
  unsaved: 'Unsaved changes',
  saving: 'Saving...',
  saved: 'Saved',
  error: 'Couldn’t save',
}

const emptyDefaultValues: SequenceForm = {
  title: '',
  steps: [{ ...defaultStep }],
}

const EMPTY_SEQUENCE_STEPS: SequenceForm['steps'] = []

const SAMPLE_PREVIEW_CONTACT: PreviewContact = {
  id: 'sample-preview-contact',
  bisonLeadId: null,
  label: 'Sample Contact',
  subtitle: 'Example Company · Decision Maker',
  tokens: {
    FIRST_NAME: 'Alex',
    LAST_NAME: 'Morgan',
    EMAIL: 'alex@examplecompany.com',
    COMPANY: 'Example Company',
    TITLE: 'Decision Maker',
  },
}

// Shared typography so the plain-text and HTML preview panes render with the
// same font, size, and line-height — toggling format should not shift the body
// visually. Only whitespace handling (plain) and rich-element styling (HTML)
// differ. We render HTML without Tailwind's `prose` (its larger line-height and
// element margins are what diverged); links/lists are styled explicitly since
// preflight strips their defaults.
const PREVIEW_BODY_BASE = 'font-sans text-sm text-fg'

function getApiErrorMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) {
    return 'Unable to save sequence. Please try again.'
  }

  const detail = err.response?.data?.detail
  if (typeof detail === 'string' && detail.length > 0) {
    return detail
  }

  return 'Unable to save sequence. Please try again.'
}

function stripHtml(value: string): string {
  // Parse via DOMParser so HTML entities decode — `getSemanticHTML()` from
  // Quill serializes apostrophes as `&#39;` and ampersands as `&amp;`, which
  // a regex-only strip would leave intact and render as literal entity text in
  // the collapsed-step first-line preview.
  const doc = new DOMParser().parseFromString(value, 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

function toPreviewContact(contact: CampaignLeadReviewItem): PreviewContact {
  const [firstName = '', ...rest] = (contact.full_name || '')
    .trim()
    .split(' ')
    .filter(Boolean)
  const lastName = rest.join(' ')
  // Only populate tokens with non-empty values — empty entries cause silent
  // substitutions in fillVariables, so a contact missing a first name would
  // ship "Hi ," with no warning. Skipping empties leaves `{FIRST_NAME}` literal
  // and surfaces in detectUnfilledVariables → "Unfilled variables" pill.
  const tokens: Record<string, string> = {}
  if (firstName) tokens.FIRST_NAME = firstName
  if (lastName) tokens.LAST_NAME = lastName
  if (contact.email) tokens.EMAIL = contact.email
  if (contact.company_name) tokens.COMPANY = contact.company_name
  if (contact.title) tokens.TITLE = contact.title
  return {
    id: `contact-${contact.contact_id}`,
    bisonLeadId: contact.bison_lead_id,
    label: `${firstName || 'Contact'} ${lastName}`.trim(),
    subtitle: [contact.company_name, contact.title].filter(Boolean).join(' · '),
    tokens,
  }
}

/**
 * Subject-line banners, extracted so each per-step instance can host its
 * own `useDebouncedFlag` calls (hooks can't run in the parent's `.map`
 * callback). Wizard-top + editor-inline banners follow the same pattern.
 */
function SubjectValidationBanners({
  unknownVariables,
  missingVariables,
}: {
  unknownVariables: ReadonlyArray<string>
  missingVariables: ReadonlyArray<EmailVariable>
}) {
  const showUnknown = useDebouncedFlag(unknownVariables.length > 0)
  const showMissing = useDebouncedFlag(missingVariables.length > 0)
  return (
    <>
      {showUnknown && unknownVariables.length > 0 && (
        <div className="mt-2 flex items-start gap-1.5 rounded-md border border-error-border bg-error-bg px-2 py-1.5 text-xs text-error-strong">
          <PiWarning className="mt-0.5 size-3 shrink-0 text-error" />
          <span>
            Subject uses variables that are not available for this campaign:{' '}
            {unknownVariables.join(', ')}
          </span>
        </div>
      )}
      {showMissing && missingVariables.length > 0 && (
        <div className="mt-2 flex items-start gap-1.5 rounded-md border border-warning-border bg-warning-bg px-2 py-1.5 text-xs text-warning-strong">
          <PiWarning className="mt-0.5 size-3 shrink-0 text-warning" />
          <span>
            Subject uses campaign column variables with missing values:{' '}
            {missingVariables
              .map(
                (variable) =>
                  `${variable.label} (${getMissingValuesLabel(variable)})`
              )
              .join(', ')}
          </span>
        </div>
      )}
    </>
  )
}

export function WizardSequenceStep({
  campaignId,
  campaignName,
  bisonCampaignId,
  onNext,
  onBack,
  stepNumber,
  totalSteps,
  readOnly = false,
  isLiveCampaign = false,
  isActiveCampaign = false,
  backLabel = 'Back',
}: WizardSequenceStepProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [expandedStepIndex, setExpandedStepIndex] = useState(0)
  const [previewStepIndex, setPreviewStepIndex] = useState(0)
  const [previewContactId, setPreviewContactId] = useState<string>('')
  const [showContactDropdown, setShowContactDropdown] = useState(false)
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)
  // Below xl the preview is an overlay pinned to the right edge; it defaults
  // to a slim collapsed bar so the editor keeps full width when the viewport
  // is too narrow for side-by-side. Ignored at xl+ (always-open column).
  const [previewCollapsed, setPreviewCollapsed] = useState(true)
  const showContactDropdownRef = useRef(showContactDropdown)
  showContactDropdownRef.current = showContactDropdown
  useMountEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showContactDropdownRef.current) {
        setShowContactDropdown(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  })
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hydratedSequenceIdRef = useRef<number | null>(null)
  const lastSavedSnapshotRef = useRef<string | null>(null)
  const saveChainRef = useRef<Promise<boolean>>(Promise.resolve(true))
  const latestDraftValuesRef = useRef<SequenceForm>(emptyDefaultValues)

  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId)
  const { data: leadReviewData } = useGetCampaignLeadReview(campaignId)
  const safeBisonCampaignId = bisonCampaignId ?? 0
  const { data: senderEmails } = useGetCampaignSenderEmails(safeBisonCampaignId)
  const senderSignature = senderEmails?.results?.[0]?.email_signature ?? ''
  const mailboxAddress = senderEmails?.results?.[0]?.email ?? null
  const multipleMailboxesAttached = (senderEmails?.results?.length ?? 0) > 1
  const { data: campaignSettings } = useGetCampaignSettings(
    campaignId,
    safeBisonCampaignId > 0
  )
  // Plain-text mode mirrors the toggle in WizardSettingsStep. Default to
  // `true` because new campaigns default to plain text — picking the wrong
  // default would briefly flash a rich-text editor on a plain-text campaign.
  const isPlainText = campaignSettings?.plain_text ?? true
  const { data: overrides } = useGetEmailOverrides(
    safeBisonCampaignId,
    safeBisonCampaignId > 0
  )

  // Set of lead IDs that have any overrides — O(1) lookup per contact in dropdown
  const overrideLeadIds = useMemo(
    () => new Set(overrides?.map((o) => o.lead_id) ?? []),
    [overrides]
  )

  const createSequenceMutation = useCreateSequence()
  const updateSequenceMutation = useUpdateSequence(campaignId)

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setError,
    reset,
    watch,
    getValues,
    setValue,
  } = useForm<SequenceForm>({
    defaultValues: emptyDefaultValues,
    mode: 'onChange',
  })
  const watchedTitle = watch('title') ?? ''

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: 'steps',
    keyName: 'fieldKey',
  })

  const watchedStepsValue = watch('steps')
  const watchedSteps = watchedStepsValue ?? EMPTY_SEQUENCE_STEPS

  const campaignVariables = useCampaignVariables(campaignId)
  const allTemplateVariables = useMemo(
    () => mergeVariables(campaignVariables),
    [campaignVariables]
  )
  const aiTokensByContact = useCampaignAITokensByContact(campaignId)

  const missingCampaignColumnVariablesInTemplate =
    findCampaignColumnVariablesWithMissingValues(
      campaignVariables,
      watchedSteps.flatMap((step) => [
        step.email_subject ?? '',
        step.email_body ?? '',
      ])
    )
  const unknownVariablesInTemplate = findUnknownTemplateVariables(
    allTemplateVariables,
    watchedSteps.flatMap((step) => [
      step.email_subject ?? '',
      step.email_body ?? '',
    ])
  )

  // Hide the top-of-wizard validation banners while the user is actively
  // typing in the editor so transient mid-token states (e.g. `{F}`) do not
  // pop them open. Submit gating still uses the live arrays so a fast
  // Next click cannot bypass validation.
  const showUnknownTemplateBanner = useDebouncedFlag(
    unknownVariablesInTemplate.length > 0
  )
  const showMissingTemplateBanner = useDebouncedFlag(
    missingCampaignColumnVariablesInTemplate.length > 0
  )

  const previewContacts = useMemo(() => {
    const includedContacts =
      leadReviewData?.results?.filter((contact) => contact.is_included) ?? []
    if (includedContacts.length > 0) {
      return includedContacts.slice(0, 50).map((contact) => {
        const base = toPreviewContact(contact)
        const aiTokens = aiTokensByContact[contact.contact_id] ?? {}
        return { ...base, tokens: { ...base.tokens, ...aiTokens } }
      })
    }
    if ((leadReviewData?.summary.total ?? 0) === 0) {
      return [SAMPLE_PREVIEW_CONTACT]
    }
    return []
  }, [leadReviewData, aiTokensByContact])

  const hasCampaignContacts = (leadReviewData?.summary.total ?? 0) > 0
  const hasIncludedPreviewContacts =
    (leadReviewData?.results?.some((contact) => contact.is_included) ??
      false) === true
  const isUsingSamplePreview = !hasCampaignContacts
  const showNoIncludedContactsState =
    hasCampaignContacts && !hasIncludedPreviewContacts

  const activePreviewContact = useMemo(() => {
    if (showNoIncludedContactsState) {
      return null
    }
    return (
      previewContacts.find((contact) => contact.id === previewContactId) ??
      previewContacts[0]
    )
  }, [previewContactId, previewContacts, showNoIncludedContactsState])

  const safePreviewStepIndex = Math.min(
    previewStepIndex,
    Math.max(0, watchedSteps.length - 1)
  )
  const previewStep = watchedSteps[safePreviewStepIndex]
  // Gap before the previewed email = the PREVIOUS step's wait_in_days
  // (EmailBison: delay after a step).
  const previewDays = watchedSteps[safePreviewStepIndex - 1]?.wait_in_days ?? 1

  // Find override for the current preview contact + step combination
  const activeOverride = useMemo(() => {
    if (
      !overrides?.length ||
      !activePreviewContact?.bisonLeadId ||
      !previewStep?.id
    ) {
      return null
    }
    return (
      overrides.find(
        (o) =>
          o.lead_id === activePreviewContact.bisonLeadId &&
          o.step_id === previewStep.id
      ) ?? null
    )
  }, [overrides, activePreviewContact?.bisonLeadId, previewStep?.id])

  // Subject is rendered as a React text node which auto-escapes; using the
  // escaping interpolateVariables path here would double-escape.
  const filledPreviewSubject = previewStep
    ? fillVariables(
        activeOverride?.custom_subject || previewStep.email_subject || '',
        activePreviewContact?.tokens ?? {}
      )
    : ''

  const previewMode = isPlainText ? 'plaintext' : 'html'

  const rawPreviewBodyHtml =
    activeOverride?.custom_body || previewStep?.email_body || ''
  // Memoize the rendered preview body — renderEmail does a full HTML parse +
  // tree walk and runs on every keystroke into the editor.
  const filledPreviewBody = useMemo(
    () =>
      previewStep
        ? renderEmail({
            bodyHtml: rawPreviewBodyHtml,
            signatureHtml: senderSignature,
            variables: activePreviewContact?.tokens ?? {},
            mode: previewMode,
          })
        : '',
    [
      previewStep,
      rawPreviewBodyHtml,
      senderSignature,
      activePreviewContact?.tokens,
      previewMode,
    ]
  )
  // "No content yet" fallback gates on the rendered body alone — checking the
  // raw template would miss the case where every body token resolves to empty
  // (e.g. `<p>{FIRST_NAME}</p>` with FIRST_NAME=""), which leaves a blank pane.
  const renderedBodyAlone = useMemo(
    () =>
      previewStep
        ? renderEmail({
            bodyHtml: rawPreviewBodyHtml,
            signatureHtml: '',
            variables: activePreviewContact?.tokens ?? {},
            mode: previewMode,
          })
        : '',
    [previewStep, rawPreviewBodyHtml, activePreviewContact?.tokens, previewMode]
  )
  const hasPreviewBodyContent = renderedBodyAlone.trim().length > 0
  const displayPreviewBody = useMemo(
    () => (isPlainText ? applyPreviewDisplayFixes(filledPreviewBody) : ''),
    [filledPreviewBody, isPlainText]
  )

  // Warning detection uses the non-escaping fill so missing `{VARNAME}` tokens
  // survive for `detectUnfilledVariables` to find — `renderEmail` collapses
  // them to empty strings.
  const bodyForWarnings = previewStep
    ? fillVariables(rawPreviewBodyHtml, activePreviewContact?.tokens ?? {})
    : ''

  const previewWarnings = useMemo(
    () =>
      Array.from(
        new Set([
          ...detectUnfilledVariables(filledPreviewSubject),
          ...detectUnfilledVariables(bodyForWarnings),
        ])
      ),
    [bodyForWarnings, filledPreviewSubject]
  )

  const currentDraftValues = useMemo<SequenceForm>(
    () => ({
      title: watchedTitle,
      steps: watchedSteps.map((step) => ({
        id: step.id,
        email_subject: step.email_subject ?? '',
        email_body: step.email_body ?? '',
        wait_in_days: step.wait_in_days ?? defaultStep.wait_in_days,
      })),
    }),
    [watchedSteps, watchedTitle]
  )

  const normalizeValues = useCallback(
    (values: SequenceForm) => {
      const normalizedTitle = values.title.trim() || campaignName.trim()
      return {
        title: normalizedTitle,
        steps: values.steps.map((step, index) => ({
          id: step.id,
          order: index + 1,
          email_subject: step.email_subject,
          email_body: step.email_body,
          wait_in_days: Math.max(1, step.wait_in_days),
        })),
      }
    },
    [campaignName]
  )

  const serializeValues = useCallback(
    (values: SequenceForm) =>
      JSON.stringify({
        sequenceId: sequence?.id ?? null,
        ...normalizeValues(values),
      }),
    [normalizeValues, sequence?.id]
  )

  const canSaveValues = (values: SequenceForm) =>
    values.steps.length > 0 &&
    values.steps.every(
      (step) =>
        step.email_subject?.trim().length > 0 &&
        stripHtml(step.email_body || '').length > 0
    )

  const currentDraftSnapshot = serializeValues(currentDraftValues)
  const currentDraftHasAnyContent =
    currentDraftValues.title.trim().length > 0 ||
    currentDraftValues.steps.some(
      (step) => step.email_subject || step.email_body
    )
  const currentDraftCanSave =
    canSaveValues(currentDraftValues) &&
    missingCampaignColumnVariablesInTemplate.length === 0 &&
    unknownVariablesInTemplate.length === 0

  useEffect(() => {
    latestDraftValuesRef.current = currentDraftValues
  }, [currentDraftValues])

  useEffect(() => {
    if (sequence && hydratedSequenceIdRef.current !== sequence.id) {
      const hydratedValues = {
        title: sequence.title || campaignName,
        steps: sequence.steps
          .sort((a, b) => a.order - b.order)
          .map((step) => ({
            id: step.id,
            email_subject: step.email_subject,
            email_body: step.email_body,
            wait_in_days: step.wait_in_days,
          })),
      }
      hydratedSequenceIdRef.current = sequence.id
      reset({
        title: hydratedValues.title,
        steps: hydratedValues.steps,
      })
      lastSavedSnapshotRef.current = serializeValues(hydratedValues)
      setSaveStatus('saved')
      setExpandedStepIndex(0)
      setPreviewStepIndex(0)
    }
  }, [campaignName, reset, sequence, serializeValues])

  useEffect(() => {
    if (sequence || !campaignName.trim()) return
    if (getValues('title').trim()) return

    setValue('title', campaignName, {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [campaignName, getValues, sequence, setValue])

  useEffect(() => {
    if (showNoIncludedContactsState || !previewContacts.length) {
      return
    }
    const hasCurrentSelection = previewContacts.some(
      (contact) => contact.id === previewContactId
    )
    if (!previewContactId || !hasCurrentSelection) {
      setPreviewContactId(previewContacts[0].id)
    }
  }, [previewContactId, previewContacts, showNoIncludedContactsState])

  const enqueueSave = useCallback(
    (
      values: SequenceForm,
      options?: { silent?: boolean }
    ): Promise<boolean> => {
      if (readOnly) {
        return Promise.resolve(true)
      }

      const snapshot = serializeValues(values)
      if (lastSavedSnapshotRef.current === snapshot) {
        return Promise.resolve(true)
      }

      saveChainRef.current = saveChainRef.current
        .catch(() => true)
        .then(async () => {
          setSaveStatus('saving')

          if (!options?.silent) {
            setIsSaving(true)
          }

          try {
            const normalized = normalizeValues(values)

            if (sequence) {
              const payload: UpdateSequencePayload = {
                title: normalized.title,
                steps: normalized.steps,
              }

              const [err] = await to(
                updateSequenceMutation.mutateAsync({
                  sequenceId: sequence.id,
                  payload,
                })
              )

              if (err) {
                handleFormError(err, setError)
                if (!options?.silent) {
                  toast.error(getApiErrorMessage(err))
                }
                setSaveStatus('error')
                return false
              }
            } else {
              const payload: CreateSequencePayload = {
                campaign_id: campaignId,
                title: normalized.title,
                steps: normalized.steps.map(
                  ({ order, email_subject, email_body, wait_in_days }) => ({
                    order,
                    email_subject,
                    email_body,
                    wait_in_days,
                  })
                ),
              }

              const [err] = await to(
                createSequenceMutation.mutateAsync(payload)
              )

              if (err) {
                handleFormError(err, setError)
                if (!options?.silent) {
                  toast.error(getApiErrorMessage(err))
                }
                setSaveStatus('error')
                return false
              }
            }

            lastSavedSnapshotRef.current = snapshot
            setSaveStatus('saved')
            return true
          } finally {
            if (!options?.silent) {
              setIsSaving(false)
            }
          }
        })

      return saveChainRef.current
    },
    [
      campaignId,
      createSequenceMutation,
      normalizeValues,
      readOnly,
      sequence,
      serializeValues,
      setError,
      updateSequenceMutation,
    ]
  )

  useEffect(() => {
    if (readOnly) {
      setSaveStatus('idle')
      return
    }

    if (!currentDraftHasAnyContent) {
      setSaveStatus(sequence ? 'saved' : 'idle')
      return
    }

    const lastSavedSnapshot = lastSavedSnapshotRef.current

    if (lastSavedSnapshot === currentDraftSnapshot) {
      setSaveStatus((current) => (current === 'saving' ? current : 'saved'))
      return
    }

    setSaveStatus('unsaved')

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
    }

    if (!currentDraftCanSave) {
      return
    }

    autosaveTimerRef.current = setTimeout(() => {
      void enqueueSave(latestDraftValuesRef.current, { silent: true })
    }, 1800)

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
  }, [
    currentDraftCanSave,
    currentDraftHasAnyContent,
    currentDraftSnapshot,
    enqueueSave,
    readOnly,
    sequence,
  ])

  const handlePrimarySubjectChange = (subject: string) => {
    setValue('steps.0.email_subject', subject, {
      shouldDirty: true,
      shouldValidate: true,
    })

    const currentSteps = getValues('steps')
    currentSteps.forEach((step, index) => {
      if (index === 0) return
      const existingSubject = step.email_subject?.trim() ?? ''
      if (existingSubject === '' || existingSubject.startsWith('Re: ')) {
        setValue(
          `steps.${index}.email_subject`,
          subject.trim() ? `Re: ${subject}` : '',
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        )
      }
    })
  }

  const addStep = () => {
    const firstSubject = getValues('steps.0.email_subject') ?? ''
    const nextIndex = watchedSteps.length
    append({
      ...defaultStep,
      wait_in_days: nextIndex === 0 ? defaultStep.wait_in_days : 3,
      email_subject: firstSubject.trim() ? `Re: ${firstSubject}` : '',
    })
    setExpandedStepIndex(nextIndex)
    setPreviewStepIndex(nextIndex)
  }

  const duplicateStep = (index: number) => {
    const step = getValues(`steps.${index}`)
    insert(index + 1, {
      email_subject: step.email_subject,
      email_body: step.email_body,
      wait_in_days: step.wait_in_days,
    })
    setExpandedStepIndex(index + 1)
    setPreviewStepIndex(index + 1)
  }

  const removeStepAtIndex = (index: number) => {
    remove(index)
    setExpandedStepIndex((current) => {
      if (current > index) return current - 1
      if (current === index) return Math.max(0, index - 1)
      return current
    })
    setPreviewStepIndex((current) => {
      if (current > index) return current - 1
      if (current === index) return Math.max(0, index - 1)
      return current
    })
  }

  const onSubmit = handleSubmit(async (values) => {
    if (readOnly) {
      onNext()
      return
    }

    if (missingCampaignColumnVariablesInTemplate.length > 0) {
      toast.warning(
        'Some campaign column variables are missing values for campaign contacts. Fill those cells before continuing.'
      )
      return
    }
    if (unknownVariablesInTemplate.length > 0) {
      toast.warning(
        'This sequence uses variables that are not available for this campaign. Remove them before continuing.'
      )
      return
    }

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
    }
    const success = await enqueueSave(values)
    if (success) {
      onNext()
    }
  })

  const canProceed =
    !readOnly &&
    isValid &&
    missingCampaignColumnVariablesInTemplate.length === 0 &&
    unknownVariablesInTemplate.length === 0 &&
    watchedSteps.length > 0 &&
    watchedSteps.every(
      (step) =>
        step.email_subject?.trim().length > 0 &&
        stripHtml(step.email_body || '').length > 0
    )

  if (isLoadingSequence) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-surface">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Build Your Sequence"
        description="Create personalized emails with a live preview."
        rightSlot={
          <div className="flex items-center gap-1.5 pt-0.5 text-sm text-fg-secondary">
            {!readOnly && saveStatus === 'saved' && (
              <PiCheck className="size-4 text-brand-active" />
            )}
            {readOnly ? 'Read-only' : SAVE_STATUS_LABEL[saveStatus]}
          </div>
        }
      />

      <div className="relative flex flex-1 flex-col overflow-hidden rounded-bl-2xl border-b border-l border-line xl:flex-row">
        <div className="flex-1 overflow-auto bg-surface-bright py-5 pl-6 pr-[4.5rem] xl:px-6">
          <div className="mx-auto max-w-[700px]">
            {isLiveCampaign && !readOnly && (
              <div className="mb-4">
                <LiveCampaignBanner />
              </div>
            )}
            {readOnly && isActiveCampaign && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-warning-border bg-warning-bg px-4 py-3 text-sm text-fg-secondary">
                This campaign is active. Pause the campaign to edit the
                sequence.
              </div>
            )}
            {readOnly && !isActiveCampaign && (
              <div className="mb-4 rounded-xl border border-line bg-surface-alt px-4 py-3 text-sm text-fg-secondary">
                This campaign has completed. The sequence is read-only.
              </div>
            )}
            {!readOnly &&
              showUnknownTemplateBanner &&
              unknownVariablesInTemplate.length > 0 && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-error-border bg-error-bg px-4 py-3 text-sm text-error-strong">
                  <PiWarning className="mt-0.5 size-4 shrink-0 text-error" />
                  <div>
                    <p className="font-medium">
                      This sequence uses variables that are not available for
                      this campaign. Remove them before continuing so emails do
                      not send with unresolved placeholders.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {unknownVariablesInTemplate.map((variable) => (
                        <code
                          key={variable}
                          className="rounded border border-error-border bg-error-bg-subtle px-1.5 py-0.5 font-mono text-xs text-error-strong"
                        >
                          {variable}
                        </code>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            {!readOnly &&
              showMissingTemplateBanner &&
              missingCampaignColumnVariablesInTemplate.length > 0 && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning-strong">
                  <PiWarning className="mt-0.5 size-4 shrink-0 text-warning" />
                  <div>
                    <p className="font-medium">
                      This sequence uses campaign column variables with missing
                      values. Fill those cells before continuing so emails do
                      not send with unresolved placeholders.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {missingCampaignColumnVariablesInTemplate.map(
                        (variable) => (
                          <span
                            key={variable.token}
                            className="rounded border border-warning-border bg-warning-bg-subtle px-1.5 py-0.5 text-xs text-warning-strong"
                          >
                            {variable.label}: {getMissingValuesLabel(variable)}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            {watchedSteps.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-brand/10">
                  <PiChatText className="size-7 text-brand" />
                </div>
                <h2 className="mb-2 text-lg font-semibold text-fg">
                  Start building your sequence
                </h2>
                <p className="mx-auto mb-6 max-w-sm text-sm text-fg-secondary">
                  Add email steps to create your outreach sequence. Each step is
                  a personalized email sent at your chosen interval.
                </p>
                <FbButton
                  onClick={addStep}
                  className="gap-2"
                  disabled={readOnly}
                >
                  <PiPlus className="size-4" />
                  Add First Email Step
                </FbButton>
              </div>
            ) : (
              <div className={cn('space-y-0', readOnly && 'select-text')}>
                {fields.map((field, index) => {
                  const step = watchedSteps[index]
                  const isExpanded = expandedStepIndex === index
                  const isFirstStep = index === 0
                  const isAutoReplyStep = !isFirstStep
                  const stepSummary = stripHtml(step?.email_body || '')
                  const missingStepSubjectVariables =
                    findCampaignColumnVariablesWithMissingValues(
                      campaignVariables,
                      [step?.email_subject ?? '']
                    )
                  const unknownStepSubjectVariables =
                    findUnknownTemplateVariables(allTemplateVariables, [
                      step?.email_subject ?? '',
                    ])

                  return (
                    <div key={field.fieldKey}>
                      {index > 0 && (
                        <div className="flex gap-3">
                          <div className="flex w-7 shrink-0 flex-col items-center">
                            <div className="w-0.5 flex-1 bg-sage-lt" />
                          </div>
                          <div className="flex items-center py-2">
                            <div className="flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-sm text-fg-secondary">
                              <PiClock className="size-3.5" />
                              <span>Wait</span>
                              {/* wait_in_days = delay AFTER a step; marker between [index-1] and [index] binds to steps[index-1]. */}
                              <input
                                type="number"
                                min={1}
                                value={
                                  watchedSteps[index - 1]?.wait_in_days ?? 1
                                }
                                disabled={readOnly}
                                onChange={(event) =>
                                  setValue(
                                    `steps.${index - 1}.wait_in_days`,
                                    Math.max(
                                      1,
                                      Number(event.target.value) || 1
                                    ),
                                    { shouldDirty: true, shouldValidate: true }
                                  )
                                }
                                className="w-10 rounded bg-surface-alt px-1 py-0.5 text-center text-sm text-fg outline-none"
                              />
                              <span>days</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <div className="flex w-7 shrink-0 flex-col items-center pt-4">
                          <div
                            className={cn(
                              'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                              step.email_subject &&
                                stripHtml(step.email_body || '')
                                ? 'bg-brand-active text-white'
                                : 'border-2 border-brand-active/30 bg-brand-active/10 text-brand-active'
                            )}
                          >
                            {index + 1}
                          </div>
                          {index < fields.length - 1 && (
                            <div className="mt-2 w-0.5 flex-1 bg-sage-lt" />
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div
                            className={cn(
                              'rounded-xl border bg-white transition-all',
                              isExpanded
                                ? 'border-brand/30 ring-2 ring-brand/15 shadow-sm'
                                : 'border-line hover:shadow-sm'
                            )}
                          >
                            <div
                              className="flex cursor-pointer items-center gap-3 px-4 py-3"
                              onClick={() =>
                                setExpandedStepIndex(isExpanded ? -1 : index)
                              }
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  {/* Drag handle hidden until step reordering is wired up. Restore <PiDotsSixVerticalBold className="size-4 shrink-0 text-fg-secondary/60" /> and its import then. */}
                                  <p className="truncate text-sm text-fg">
                                    {step.email_subject?.trim() ? (
                                      step.email_subject
                                    ) : (
                                      <span className="italic text-fg-secondary">
                                        No subject line
                                      </span>
                                    )}
                                  </p>
                                </div>
                                {!isExpanded && stepSummary && (
                                  <p className="mt-0.5 truncate text-sm text-fg-secondary">
                                    {stepSummary}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  className="flex size-7 items-center justify-center rounded hover:bg-surface-alt text-fg-secondary"
                                  disabled={readOnly}
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    duplicateStep(index)
                                  }}
                                  title="Duplicate step"
                                >
                                  <PiCopy className="size-3.5" />
                                </button>
                                {fields.length > 1 && (
                                  <button
                                    type="button"
                                    className="flex size-7 items-center justify-center rounded hover:bg-error-bg text-fg-secondary hover:text-error"
                                    disabled={readOnly}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      removeStepAtIndex(index)
                                    }}
                                    title="Remove step"
                                  >
                                    <PiTrash className="size-3.5" />
                                  </button>
                                )}
                                {isExpanded ? (
                                  <PiCaretUp className="size-4 text-fg-secondary" />
                                ) : (
                                  <PiCaretDown className="size-4 text-fg-secondary" />
                                )}
                              </div>
                            </div>

                            {isExpanded && (
                              <div
                                className={cn(
                                  'space-y-4 border-t border-line px-4 pb-4',
                                  readOnly && 'pointer-events-none'
                                )}
                              >
                                <div className="pt-4">
                                  <div className="mb-1.5 flex items-center gap-2">
                                    <label className="text-sm text-fg-secondary">
                                      Subject line
                                    </label>
                                    {isAutoReplyStep && (
                                      <span className="inline-flex items-center rounded-full bg-ds2026-badge-primary-bg px-2 py-0.5 text-xs font-medium text-ds2026-badge-primary-text">
                                        Auto Re: from Step 1
                                      </span>
                                    )}
                                  </div>
                                  <BaseTextInput
                                    name={`steps.${index}.email_subject`}
                                    value={step.email_subject ?? ''}
                                    onChange={(value) => {
                                      if (index === 0) {
                                        handlePrimarySubjectChange(
                                          String(value)
                                        )
                                        return
                                      }
                                      setValue(
                                        `steps.${index}.email_subject`,
                                        String(value),
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        }
                                      )
                                    }}
                                    placeholder={
                                      isFirstStep
                                        ? 'Email subject line — use {FIRST_NAME} or any campaign column variable'
                                        : 'Re: [auto-generated from step 1]'
                                    }
                                    noMargin
                                  />
                                  <SubjectValidationBanners
                                    unknownVariables={
                                      unknownStepSubjectVariables
                                    }
                                    missingVariables={
                                      missingStepSubjectVariables
                                    }
                                  />
                                </div>

                                <div>
                                  <div className="mb-1.5 flex items-center justify-between gap-2">
                                    <label className="block text-sm text-fg-secondary">
                                      Email body
                                    </label>
                                    <FormatBadge plainText={isPlainText} />
                                  </div>
                                  <EmailBodyEditor
                                    control={control}
                                    name={`steps.${index}.email_body`}
                                    label=""
                                    placeholder={
                                      'Write your email content here...\n\nUse variables like {FIRST_NAME} to personalize.'
                                    }
                                    hideHelperText
                                    disableToolbar={readOnly}
                                    plainText={isPlainText}
                                    campaignId={campaignId}
                                  />
                                  {index === 0 && (
                                    <p className="mt-1.5 text-sm text-fg-secondary">
                                      Tip: include casual opt-out language
                                      naturally — e.g.{' '}
                                      <span className="italic">
                                        &ldquo;Just reply if you&rsquo;d rather
                                        not hear from me.&rdquo;
                                      </span>{' '}
                                      Vary the wording across campaigns to avoid
                                      pattern matching.
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

                {!readOnly && (
                  <div className="pt-2">
                    <div className="flex w-7 justify-center">
                      <div className="h-8 w-0.5 bg-sage-lt" />
                    </div>
                    <div className="mt-1 flex items-center gap-3">
                      <div className="flex w-7 shrink-0 justify-center">
                        <div className="flex size-7 items-center justify-center rounded-full border-2 border-dashed border-brand/30">
                          <PiPlus className="size-3.5 text-brand/50" />
                        </div>
                      </div>
                      <FbButton
                        variant="outline"
                        onClick={addStep}
                        className="gap-2"
                      >
                        <PiPlus className="size-4" />
                        Add Email Step
                      </FbButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {watchedSteps.length > 0 && (
          <div
            className={cn(
              'absolute bottom-0 right-0 top-0 z-30 flex shrink-0 flex-col rounded-tl-xl border-l border-t border-line bg-surface-alt shadow-xl transition-[width] duration-200',
              previewCollapsed ? 'w-12' : 'w-[min(34rem,92%)]',
              'xl:static xl:z-auto xl:w-[520px] xl:shadow-none 2xl:w-[580px]'
            )}
          >
            {previewCollapsed && (
              <button
                type="button"
                onClick={() => setPreviewCollapsed(false)}
                aria-label="Expand live preview"
                className="group flex h-full w-full flex-col items-center gap-3 rounded-tl-xl bg-white py-4 text-fg-secondary transition-colors hover:bg-neutral-50 xl:hidden"
              >
                <PiCaretDoubleLeft className="size-4 text-fg-secondary transition-colors group-hover:text-fg" />
                <PiEye className="size-4 text-neutral-500" />
                <span className="text-sm text-fg-secondary [writing-mode:vertical-rl]">
                  Live preview
                </span>
              </button>
            )}
            {!previewCollapsed && (
              <button
                type="button"
                onClick={() => setPreviewCollapsed(true)}
                aria-label="Collapse live preview"
                className="absolute left-0 top-4 flex h-14 w-6 -translate-x-full items-center justify-center rounded-l-lg border border-r-0 border-line bg-white text-fg-secondary shadow-md transition-colors hover:bg-neutral-50 hover:text-fg xl:hidden"
              >
                <PiCaretDoubleRight className="size-3.5" />
              </button>
            )}
            <div
              className={cn(
                'flex min-h-0 flex-1 flex-col',
                previewCollapsed && 'hidden xl:flex'
              )}
            >
              <div className="space-y-3 rounded-tl-xl border-b border-line bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PiEye className="size-4 text-neutral-500" />
                    <span className="mt-px text-sm leading-none text-fg-secondary">
                      Live preview
                    </span>
                    <FormatBadge plainText={isPlainText} />
                  </div>
                  <div className="flex items-center gap-2">
                    {previewWarnings.length > 0 && (
                      <StatusBadge color="warning" icon={PiWarning}>
                        Unfilled variables
                      </StatusBadge>
                    )}
                    <FloatingTooltip
                      text={
                        !previewStep?.id
                          ? 'Save the sequence first to send a test'
                          : `Send Step ${safePreviewStepIndex + 1} as a test`
                      }
                    >
                      <button
                        type="button"
                        onClick={() => setShowTestEmailModal(true)}
                        disabled={readOnly || !previewStep?.id}
                        className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-brand-bright transition-colors hover:text-brand-active disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-brand-bright"
                      >
                        <PiPaperPlaneTilt className="size-3.5" />
                        Send test email
                      </button>
                    </FloatingTooltip>
                  </div>
                </div>

                <div className="relative">
                  <button
                    type="button"
                    disabled={
                      showNoIncludedContactsState || previewContacts.length <= 1
                    }
                    onClick={() => setShowContactDropdown((open) => !open)}
                    className="flex w-full items-center gap-2 rounded-lg border border-line bg-white px-2.5 py-1.5 text-left transition hover:bg-neutral-50 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <div className="flex size-6 items-center justify-center rounded-full bg-brand-sage-100">
                      <PiUser className="size-3 text-brand-sage-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-fg-secondary">
                        {activeOverride
                          ? 'Custom email for'
                          : 'Variables filled as'}
                      </p>
                      <p className="truncate text-sm text-fg">
                        {activePreviewContact?.label ??
                          (isUsingSamplePreview
                            ? 'Sample Contact'
                            : 'No included contacts')}
                        {(activePreviewContact?.subtitle ||
                          (isUsingSamplePreview
                            ? 'Example preview until campaign contacts are added'
                            : showNoIncludedContactsState
                              ? 'Go back and include at least one contact'
                              : 'Preview contact')) && (
                          <span className="ml-1 text-sm text-fg-secondary">
                            ·{' '}
                            {activePreviewContact?.subtitle ||
                              (isUsingSamplePreview
                                ? 'Example preview until campaign contacts are added'
                                : showNoIncludedContactsState
                                  ? 'Go back and include at least one contact'
                                  : 'Preview contact')}
                          </span>
                        )}
                      </p>
                    </div>
                    <PiCaretDown
                      className={cn(
                        'size-4 shrink-0 text-fg-secondary transition-transform',
                        showContactDropdown && 'rotate-180'
                      )}
                    />
                  </button>
                  {showContactDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowContactDropdown(false)}
                      />
                      <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-line bg-white py-1 shadow-lg">
                        <p className="px-3 py-1.5 text-sm text-fg-secondary">
                          Preview as contact
                        </p>
                        {previewContacts.map((contact) => (
                          <button
                            key={contact.id}
                            type="button"
                            onClick={() => {
                              setPreviewContactId(contact.id)
                              setShowContactDropdown(false)
                            }}
                            className={cn(
                              'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-surface-alt',
                              previewContactId === contact.id && 'bg-brand/5'
                            )}
                          >
                            <div className="flex size-6 items-center justify-center rounded-full bg-brand/10">
                              <PiUser className="size-3 text-brand" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm text-fg">
                                {contact.label}
                                {contact.bisonLeadId != null &&
                                  overrideLeadIds.has(contact.bisonLeadId) && (
                                    <StatusBadge
                                      color="info"
                                      className="ml-1.5"
                                    >
                                      Edited
                                    </StatusBadge>
                                  )}
                              </p>
                              <p className="truncate text-sm text-fg-secondary">
                                {contact.subtitle}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {watchedSteps.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewStepIndex((index) => Math.max(0, index - 1))
                      }
                      disabled={safePreviewStepIndex === 0}
                      className="flex size-7 items-center justify-center rounded-lg border border-line transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <PiCaretLeft className="size-3.5" />
                    </button>
                    <div className="flex flex-1 items-center justify-center gap-1.5">
                      {watchedSteps.map((_, index) => (
                        <button
                          key={`preview-step-${index}`}
                          type="button"
                          onClick={() => setPreviewStepIndex(index)}
                          className={cn(
                            'size-2 rounded-full transition-all',
                            index === safePreviewStepIndex
                              ? 'scale-125 bg-brand'
                              : 'bg-fg-secondary/20 hover:bg-fg-secondary/40'
                          )}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewStepIndex((index) =>
                          Math.min(watchedSteps.length - 1, index + 1)
                        )
                      }
                      disabled={
                        safePreviewStepIndex === watchedSteps.length - 1
                      }
                      className="flex size-7 items-center justify-center rounded-lg border border-line transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <PiCaretRight className="size-3.5" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-auto p-4">
                {showNoIncludedContactsState ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="max-w-xs rounded-xl border border-dashed border-line bg-white px-6 py-8 text-center">
                      <PiUser className="mx-auto mb-3 size-8 text-fg-secondary/40" />
                      <h3 className="text-sm font-semibold text-fg">
                        No included preview contacts
                      </h3>
                      <p className="mt-2 text-sm text-fg-secondary">
                        Include at least one contact in Review Leads to preview
                        your sequence with real campaign recipients.
                      </p>
                    </div>
                  </div>
                ) : (
                  previewStep && (
                    <div className="overflow-hidden rounded-xl border border-line bg-white shadow-sm">
                      <div className="flex items-center gap-2 border-b border-line bg-neutral-50 px-4 py-2.5">
                        <div className="flex size-6 items-center justify-center rounded-full bg-brand-active text-xs text-white">
                          {safePreviewStepIndex + 1}
                        </div>
                        <span className="text-sm text-fg-secondary">
                          Email Step {safePreviewStepIndex + 1} of{' '}
                          {watchedSteps.length}
                        </span>
                        {activeOverride && (
                          <StatusBadge color="info">Edited</StatusBadge>
                        )}
                        {safePreviewStepIndex > 0 && (
                          <span className="ml-auto flex items-center gap-1 text-xs text-fg-secondary">
                            <PiClock className="size-2.5" />
                            {previewDays} day{previewDays === 1 ? '' : 's'}{' '}
                            after Step {safePreviewStepIndex}
                          </span>
                        )}
                      </div>

                      <div className="border-b border-line px-4 py-3">
                        <p className="mb-1 text-sm text-fg-secondary">
                          Subject
                        </p>
                        {filledPreviewSubject ? (
                          <p className="text-sm text-fg">
                            {filledPreviewSubject}
                          </p>
                        ) : (
                          <p className="text-sm italic text-fg-secondary">
                            No subject line
                          </p>
                        )}
                      </div>

                      <div className="px-4 py-4">
                        <p className="mb-2 text-sm text-fg-secondary">Body</p>
                        {hasPreviewBodyContent ? (
                          isPlainText ? (
                            <div
                              className={cn(
                                PREVIEW_BODY_BASE,
                                'whitespace-pre-wrap [hyphens:none] [overflow-wrap:normal] [word-break:normal]'
                              )}
                            >
                              {displayPreviewBody}
                            </div>
                          ) : (
                            <div
                              className={cn(
                                PREVIEW_BODY_BASE,
                                'max-w-full break-words [overflow-wrap:break-word]',
                                '[&_a]:text-brand [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5'
                              )}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(filledPreviewBody),
                              }}
                            />
                          )
                        ) : (
                          <div className="py-8 text-center text-sm italic text-fg-secondary">
                            <PiChatText className="mx-auto mb-2 size-6 opacity-30" />
                            No content yet - start typing in the editor
                          </div>
                        )}
                      </div>

                      {previewWarnings.length > 0 && (
                        <div className="border-t border-warning-border bg-warning-bg-subtle px-4 py-3">
                          <div className="flex items-start gap-2">
                            <PiWarning className="mt-0.5 size-3.5 shrink-0 text-warning" />
                            <div>
                              <p className="text-xs text-fg-secondary">
                                Some variables could not be filled for this
                                preview contact:
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {previewWarnings.map((warning) => (
                                  <code
                                    key={warning}
                                    className="rounded bg-warning-bg px-1.5 py-0.5 text-xs text-fg-secondary"
                                  >
                                    {warning}
                                  </code>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}

                {previewStep &&
                  activePreviewContact &&
                  !showNoIncludedContactsState && (
                    <div className="mt-3 rounded-lg bg-white/80 px-3 py-2 text-sm text-fg-secondary">
                      <p className="flex items-center gap-2 leading-relaxed">
                        <PiUser className="size-3.5 shrink-0 text-fg-secondary" />
                        <span>
                          Variables filled as{' '}
                          <span className="font-medium text-fg">
                            {activePreviewContact.label}
                          </span>
                          {activePreviewContact.subtitle
                            ? ` at ${activePreviewContact.subtitle}`
                            : '.'}
                        </span>
                      </p>
                      <SignatureDisclaimer
                        mailboxAddress={mailboxAddress}
                        multipleMailboxesAttached={multipleMailboxesAttached}
                      />
                      {isUsingSamplePreview && (
                        <p className="mt-1.5 flex items-center gap-2 leading-relaxed">
                          <PiWarning className="size-3.5 shrink-0 text-fg-secondary" />
                          <span>
                            This is a sample preview. Add campaign contacts to
                            see real lead substitutions here.
                          </span>
                        </p>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>

      {bisonCampaignId != null && bisonCampaignId > 0 && (
        <SendStepTestEmailModal
          show={showTestEmailModal}
          onHide={() => setShowTestEmailModal(false)}
          bisonStepId={previewStep?.id ?? null}
          bisonCampaignId={bisonCampaignId}
          stepNumber={safePreviewStepIndex + 1}
          unresolvedVariables={previewWarnings}
        />
      )}

      <WizardStepFooter>
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <PiCaretLeft className="size-4" />
          {backLabel}
        </FbButton>
        {!readOnly && (
          <FbButton
            type="button"
            onClick={() => void onSubmit()}
            disabled={!canProceed || isSaving}
            className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="size-4" />
                Saving...
              </>
            ) : (
              <>
                Continue to Settings
                <PiCaretRight className="size-4" />
              </>
            )}
          </FbButton>
        )}
      </WizardStepFooter>
    </div>
  )
}
